import { useQuery } from '@tanstack/react-query';
import { CopyIcon, TrashIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmRevokeShareDialog } from '@/features/chat-share/ConfirmRevokeShareDialog.js';
import {
  activeAutomationShares,
  automationShareUrl,
  automationSharesQueryOptions,
  useRevokeAutomationShare,
  type AutomationShareSubject,
  type AutomationShareView,
} from '@/lib/api/automation-shares.js';
import { rememberedGroupName } from '@/lib/api/directory-groups.js';
import { copyToClipboard } from '@/lib/clipboard.js';

/** Date + time, so links created the same day are distinguishable. */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The mode label a share row shows — the same words the outside tab's radio options use. */
function modeLabel(share: AutomationShareView): string {
  return share.accessMode === 'public'
    ? 'Anyone with the link'
    : 'Specific people or groups';
}

/**
 * One recipient of a `named` share, in words.
 *
 * A person the directory could not resolve is shown as their own id — it is all there is, and it is at
 * least a handle the author can search for. A group is different: its `displayName` is null unless the
 * service resolved it, so the name falls back to the picker that addressed the share, and where nothing
 * remembers it the row says plainly that the recipient is a group. An Entra object id on screen would
 * tell the author nothing about who has the link.
 */
function subjectLabel(subject: AutomationShareSubject): string {
  if (subject.subjectType !== 'entra_group') {
    return subject.displayName ?? subject.subjectId;
  }
  const name = subject.displayName ?? rememberedGroupName(subject.subjectId);
  return name !== null ? `${name} (group)` : 'A group (name unavailable)';
}

/** Who a `named` share is addressed to, by name, comma-separated. */
function recipientsLine(subjects: readonly AutomationShareSubject[]): string {
  return subjects.map(subjectLabel).join(', ');
}

/**
 * An automation's own active share links: mode, optional name, created date, and — for a `named` link —
 * the recipients by name on a second line. Reads its own list (`automationSharesQueryOptions`), so it
 * stays current whether the caller just created a link in this same dialog or opened it on one created
 * earlier.
 *
 * Revoke asks first, through the same dialog the chat and dashboard share lists use: the trash icon
 * sits in a dense row, and a misclick kills a URL that may already be in a customer's hands. A revoked
 * link cannot be brought back — a new share is a new id.
 *
 * `onRevoked` fires once the service has accepted a revoke, so a caller still showing that link's URL
 * can stop showing it rather than offer a dead address to copy.
 */
export function AutomationShareList({
  automationId,
  onRevoked,
}: {
  automationId: string;
  onRevoked?: (shareId: string) => void;
}): JSX.Element {
  const sharesQuery = useQuery(automationSharesQueryOptions(automationId));
  const revoke = useRevokeAutomationShare();
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);
  const shares = activeAutomationShares(sharesQuery.data ?? []);

  if (sharesQuery.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading…</p>;
  }
  if (sharesQuery.isError) {
    return (
      <p className="text-xs text-muted-foreground">Couldn&apos;t load links.</p>
    );
  }
  if (shares.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No active links yet.</p>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-border/40 border-t border-border pt-2">
      {shares.map((share) => (
        <div key={share.id} className="flex flex-col gap-1 py-2">
          <div className="flex items-center gap-2">
            <Badge
              variant={share.accessMode === 'public' ? 'secondary' : 'outline'}
            >
              {modeLabel(share)}
            </Badge>
            {share.name ? (
              <span className="min-w-0 truncate text-xs font-medium text-foreground">
                {share.name}
              </span>
            ) : null}
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {formatDateTime(share.createdAt)}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Copy share link"
                onClick={() =>
                  void copyToClipboard(
                    automationShareUrl(share.id),
                    'Link copied',
                  )
                }
              >
                <CopyIcon className="size-4" strokeWidth={1.75} />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Revoke share"
                onClick={() => setPendingRevokeId(share.id)}
              >
                <TrashIcon className="size-4" strokeWidth={1.75} />
              </Button>
            </div>
          </div>
          {share.accessMode === 'named' ? (
            <span className="break-words text-xs text-muted-foreground">
              {recipientsLine(share.subjects)}
            </span>
          ) : null}
        </div>
      ))}
      <ConfirmRevokeShareDialog
        open={pendingRevokeId !== null}
        onOpenChange={(next) => {
          if (!next) setPendingRevokeId(null);
        }}
        pending={revoke.isPending}
        onConfirm={() => {
          const shareId = pendingRevokeId;
          setPendingRevokeId(null);
          if (shareId === null) return;
          revoke.mutate(
            { automationId, shareId },
            { onSuccess: () => onRevoked?.(shareId) },
          );
        }}
      />
    </div>
  );
}
